import { computed, Directive, input, output } from '@angular/core';
import { FormUiControl, ValidationError } from '@angular/forms/signals';

/**
 * Module-scoped so every control that renders without a `name` gets its own id. A
 * per-instance counter would hand every one of them `juno-control-0`, which duplicates
 * ids in the DOM and, for radios, merges two nameless groups into one browser group.
 */
let nextControlSequence = 0;

/**
 * Everything a Juno form control needs that is **not** its value.
 *
 * Angular models the two control contracts as siblings on top of one shared
 * base: `FormValueControl<T>` and `FormCheckboxControl` both extend
 * `FormUiControl`, which declares only the optional state properties
 * (`errors`, `touched`, `disabled`, …). This class is the Juno mirror of that
 * base, so the state block is declared exactly once and the two subclasses add
 * nothing but their required member:
 *
 * ```
 *              FormControlStateAbstract        (this class → FormUiControl)
 *                   /                \
 *   InputValueClassAbstract      FormCheckClassAbstract
 *      value = model<T>()          checked = model<boolean>()
 * ```
 *
 * The split is not stylistic, it is forced by the contracts. `FormValueControl`
 * declares `checked?: undefined` and `FormCheckboxControl` declares
 * `value?: undefined`, so a single class holding both members would satisfy
 * neither. Keeping both out of this class is what lets it be shared.
 *
 * @template CL The shape of the optional `appliedClasses` object that lets
 *              consumers customise internal CSS classes at the call site.
 *              - `JunoInput`  → `string` (one extra class for the wrapper)
 *              - `JunoSelect` → `{ selectedClasses: string; optionClasses: string }`
 *
 * @implements {FormUiControl<unknown>}
 *
 * @usageNotes
 * Never extend this class directly from a component. Pick the subclass that
 * matches the contract the control needs:
 *
 * | Control                                   | Extend                    | Contract              |
 * |-------------------------------------------|---------------------------|-----------------------|
 * | text / number / select / **radio group**  | `InputValueClassAbstract` | `FormValueControl<T>` |
 * | multi-checkbox group (value is an array)  | `InputValueClassAbstract` | `FormValueControl<T[]>` |
 * | single checkbox / toggle / switch         | `FormCheckClassAbstract`  | `FormCheckboxControl` |
 *
 * Shared behavior in this base:
 * - `ariaLabel` provides an accessible name when no visible `label` is shown.
 * - `showError` keeps invalid visuals and accessibility state in sync while
 *   suppressing errors for disabled controls.
 * - `controlId` is the single id source for `id`, `for`, `aria-labelledby`
 *   and `aria-describedby`, preferring Signal Forms `name` and falling back to
 *   a generated id.
 *
 * - `FormUiControl` also declares `readonly`, `hidden`, `invalid`,
 *   `disabledReasons`, and `maxLength`. All except `maxLength` are deliberately
 *   left out here: an input that no template renders is dead weight. Add one to
 *   this class the day a control actually reads it, and it becomes available to
 *   every control at once. `maxLength` is included because `JunoInput` binds it
 *   to the native `<input>` and any future control with a length limit may need
 *   it too.
 *
 * - `onGroupFocusOut`
 *    Marks the control group as touched when focus leaves the group container.
 *    Bind this handler to a wrapper `focusout` event. Moving focus between
 *    elements inside the same wrapper does not emit `touch`.
 *
 * @param event Native `focusout` event from the group host element.
 *
 * @see {@link InputValueClassAbstract}
 * @see {@link FormCheckClassAbstract}
 * @see [Custom Controls](https://angular.dev/guide/forms/signals/custom-controls)
 */
@Directive()
export abstract class FormControlStateAbstract<CL = unknown> implements FormUiControl<unknown> {
  required = input<boolean>(false);
  pattern = input<readonly RegExp[]>([]);
  errors = input<readonly ValidationError.WithOptionalFieldTree[]>([]);
  pending = input<boolean>(false);
  touched = input<boolean>(false);
  touch = output<void>();
  disabled = input<boolean>(false);
  dirty = input<boolean>(false);
  name = input<string>('');
  appliedClasses = input<CL>();
  label = input<string>('');
  labelClass = input<string>('');
  ariaLabel = input<string>('');
  maxLength = input<number | undefined>();
  readonly showError = computed(
    () => !this.disabled() && (this.touched() || this.dirty()) && !this.pending() && this.errors().length > 0
  );

  readonly #fallbackId = `juno-control-${nextControlSequence++}`;
  readonly controlId = computed(() => this.name() || this.#fallbackId);

  readonly onGroupFocusOut = (event: FocusEvent): void => {
    const host = event.currentTarget as HTMLElement;
    const nextFocused = event.relatedTarget as Node | null;

    if (!nextFocused || !host.contains(nextFocused)) {
      this.touch.emit();
    }
  };
}
